import React from "react";
import { Box, Image, Text, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import TicketButton from "./TicketButton";
import Ticket from "../images/Menu/Ticket.png";
import { TEXTS } from "../constants/texts";

interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

// InViewHookResponseの型定義
interface InViewHookResponse {
  ref: (node?: Element) => void;
  inView: boolean;
  entry?: IntersectionObserverEntry;
}

interface TicketSectionProps {
  refs: {
    ticketRef: InViewHookResponse;
    ticketContentRef: InViewHookResponse;
    generalSaleTitleRef: InViewHookResponse;
    generalSaleContentRef: InViewHookResponse;
    generalSaleButtonRef: InViewHookResponse;
    ticketContent8Ref: InViewHookResponse;
    ticketContent9Ref: InViewHookResponse;
    ticketContent10Ref: InViewHookResponse;
    ticketContent11Ref: InViewHookResponse;
    ticketContent16Ref: InViewHookResponse;
    [key: string]: InViewHookResponse;
  };
  animationStates: Record<string, "visible" | "hidden" | "instant">;
  slideInVariants: any;
  elementsAnimatedState: {
    ticketContent: boolean;
    ticketContent8: boolean;
    ticketContent9: boolean;
    ticketContent10: boolean;
    [key: string]: boolean;
  };
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  scrollDirection: "up" | "down";
}

const TicketSection: React.FC<TicketSectionProps> = ({
  refs,
  animationStates,
  elementsAnimatedState,
  setIsModalOpen,
  scrollDirection,
}) => {
  // 統一されたアニメーションのバリアント定義
  const unifiedVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      transition: { duration: 0 },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: { duration: 0 },
    },
  };

  // 時間差アニメーション用のバリアント
  const staggerParentVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1,
      },
    },
    instant: {
      opacity: 1,
      transition: { duration: 0 },
    },
  };

  const staggerChildVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      transition: { duration: 0 },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: { duration: 0 },
    },
  };

  return (
    <Box
      id="ticket-section"
      pos="relative"
      w="100%"
      mb={{ base: "80px", sm: "90px", md: "100px", lg: "120px" }}
    >
      <Box
        ref={refs.ticketRef.ref}
        width="100%"
        textAlign="center"
        mb={{
          base: "40px",
          sm: "50px",
          md: "60px",
          lg: "80px",
        }}
      >
        <motion.div
          initial="hidden"
          animate={animationStates.ticket}
          variants={unifiedVariants}
        >
          <Image
            src={Ticket}
            alt="ticket"
            w={{
              base: "90%",
              sm: "80%",
              md: "80%",
              lg: "80%",
              xl: "70%",
              "2xl": "65%",
            }}
            h="auto"
            maxW={{
              base: "360px",
              sm: "450px",
              md: "700px",
              lg: "900px",
              xl: "1200px",
              "2xl": "1200px",
            }}
            mx="auto"
            display="block"
            {...commonImageProps}
          />
        </motion.div>
      </Box>

      {/* チケットのコンテンツ部分 */}
      <Box
        ref={refs.ticketContentRef.ref}
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
        }}
        mx="auto"
      >
        <motion.div
          initial="hidden"
          animate={animationStates.ticketContent}
          variants={staggerParentVariants}
        >
          <VStack spacing={2} align="center">
            {/* メインタイトル */}
            <motion.div variants={staggerChildVariants}>
              <Text
                color="rgb(215, 200, 150)"
                fontSize={{
                  base: "14px",
                  sm: "20px",
                  md: "28px",
                  lg: "35px",
                  xl: "35px",
                }}
                fontWeight="bold"
                whiteSpace={{
                  base: "normal",
                  md: "nowrap",
                }}
                mt={{
                  base: "-30px",
                  sm: "-40px",
                  md: "-40px",
                  lg: "-60px",
                  xl: "-60px",
                }}
              >
                {TEXTS.Ticket.content[0]}
              </Text>
            </motion.div>

            {/* 価格情報 */}
            <motion.div variants={staggerChildVariants}>
              <Flex
                gap={{
                  base: 2,
                  sm: 3,
                  md: 6,
                  lg: 10,
                  xl: 14,
                }}
                align="center"
                flexWrap="wrap"
                justifyContent="center"
                mt={{
                  base: "0",
                  sm: "0",
                  md: "2",
                  lg: "2",
                }}
              >
                {[1, 2, 3].map((index) => (
                  <Text
                    key={`ticket-price-text-${index}`}
                    color="white"
                    fontSize={{
                      base: "14px",
                      sm: "20px",
                      md: "28px",
                      lg: "35px",
                      xl: "35px",
                    }}
                    fontWeight="bold"
                  >
                    {TEXTS.Ticket.content[index]}
                  </Text>
                ))}
              </Flex>
            </motion.div>

            {/* 下線付きテキスト */}
            <motion.div variants={staggerChildVariants}>
              <Box
                color="white"
                fontSize={{
                  base: "10px",
                  sm: "10px",
                  md: "18px",
                  lg: "24px",
                  xl: "24px",
                }}
                borderBottom="1px solid white"
                pb={0}
                width="fit-content"
                mt={{
                  base: "0",
                  sm: "0",
                  md: "4",
                  lg: "4",
                }}
                textAlign="center"
                mx="auto"
              >
                {TEXTS.Ticket.content[4]}
              </Box>
            </motion.div>

            {/* 先行販売 */}
            <Box
              bg="rgb(91, 63, 48)"
              px={{ base: 4, sm: 6, md: 8, lg: 10 }}
              py={{ base: 2, sm: 2, md: 2, lg: 2 }}
              mt={{ base: "2", sm: "4", md: "6", lg: "10" }}
              width="100%"
              mx="auto"
            >
              <Text
                color="white"
                fontSize={{
                  base: "14px",
                  sm: "20px",
                  md: "28px",
                  lg: "35px",
                  xl: "35px",
                }}
                fontWeight="bold"
                textAlign="center"
              >
                <motion.div
                  initial="hidden"
                  animate={animationStates.ticketContent}
                  variants={{
                    hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                        delay: 0.6,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                >
                  {TEXTS.Ticket.content[11]}
                </motion.div>
              </Text>
            </Box>

            {/* 茶色の背景コンテンツ部分 */}
            <Box
              bg="rgb(105, 82, 63)"
              width="100%"
              mx="auto"
              mt={-2}
              minHeight={{
                base: "200px",
                sm: "240px",
                md: "280px",
                lg: "320px",
              }}
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={{ base: 4, sm: 5, md: 6, lg: 8 }}
            >
              <motion.div
                initial="hidden"
                animate={animationStates.ticketContent}
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      delay: 0.7,
                      delayChildren: 0.8,
                      staggerChildren: 0.15,
                    },
                  },
                  instant: { opacity: 1, transition: { duration: 0 } },
                }}
                style={{ width: "100%" }}
              >
                <VStack
                  spacing={{ base: 1, sm: 1, md: 1, lg: 1 }}
                  width="100%"
                  align="center"
                  justify="center"
                >
                  {/* WOWOW WEB会員先行 (抽選) */}
                  <motion.div
                    variants={staggerChildVariants}
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "20px",
                        md: "28px",
                        lg: "35px",
                        xl: "35px",
                      }}
                      fontWeight="bold"
                      textAlign="center"
                    >
                      <Flex align="center" justify="center" gap={0}>
                        {TEXTS.Ticket.content[12]}
                        <Text
                          fontSize={{
                            base: "10px",
                            sm: "14px",
                            md: "20px",
                            lg: "25px",
                            xl: "25px",
                          }}
                          fontWeight="normal"
                        >
                          {TEXTS.Ticket.content[26]}
                        </Text>
                      </Flex>
                    </Text>
                  </motion.div>

                  {/* お申込みにはWOWOWのWEBアカウント... */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontSize={{
                        base: "8px",
                        sm: "10px",
                        md: "14px",
                        lg: "16px",
                        xl: "18px",
                        "2xl": "18px",
                      }}
                      textDecoration="underline"
                    >
                      <Flex
                        as="span"
                        cursor="pointer"
                        onClick={() => setIsModalOpen(true)}
                        _hover={{ color: "rgb(215, 200, 150)" }}
                        align="center"
                        justify="center"
                        gap={1}
                        bg="rgb(66, 47, 30)"
                        px={2}
                        py={1}
                        borderRadius="md"
                        border="1px solid rgba(255, 255, 255, 0.3)"
                        display="inline-flex"
                      >
                        {TEXTS.Ticket.content[13]}
                        <Box
                          as="span"
                          fontSize="0.8em"
                          transform="translateY(1px)"
                        ></Box>
                      </Flex>
                    </Text>
                  </motion.div>

                  {/* 受付期間 */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                        "2xl": "24px",
                      }}
                      mb={{
                        base: -7,
                        sm: -7,
                        md: -6,
                        lg: -6,
                      }}
                    >
                      {TEXTS.Ticket.content[14]}
                    </Text>
                  </motion.div>

                  {/* お申込みボタン */}
                  <motion.div
                    variants={staggerChildVariants}
                    style={{ marginTop: "0px" }}
                  >
                    <Box
                      mt={{
                        base: "-10px",
                        sm: "-10px",
                        md: "-10px",
                        lg: "-12px",
                      }}
                    >
                      <TicketButton
                        text={TEXTS.Ticket.content[29]}
                        color="#3C3C3C"
                        isActive={false}
                      />
                    </Box>
                  </motion.div>

                  {/* TBSラジオ先行 */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontWeight="bold"
                      fontSize={{
                        base: "14px",
                        sm: "20px",
                        md: "28px",
                        lg: "35px",
                        xl: "35px",
                      }}
                      mt={{
                        base: -3,
                        sm: -3,
                        md: 0,
                        lg: 0,
                      }}
                      textAlign="center"
                    >
                      <Flex align="center" justify="center" gap={0}>
                        {TEXTS.Ticket.content[28]}
                        <Text
                          fontSize={{
                            base: "10px",
                            sm: "14px",
                            md: "20px",
                            lg: "25px",
                            xl: "25px",
                          }}
                          fontWeight="normal"
                        >
                          {TEXTS.Ticket.content[26]}
                        </Text>
                      </Flex>
                    </Text>
                  </motion.div>

                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                        "2xl": "24px",
                      }}
                      mb={{
                        base: -7,
                        sm: -7,
                        md: -6,
                        lg: -6,
                      }}
                      textAlign="center"
                    >
                      {TEXTS.Ticket.content[14]}
                    </Text>
                  </motion.div>

                  {/* TBSラジオ先行用ボタン */}
                  <motion.div
                    variants={staggerChildVariants}
                    style={{ marginTop: "0px" }}
                  >
                    <Box
                      mt={{
                        base: "-10px",
                        sm: "-10px",
                        md: "-10px",
                        lg: "-12px",
                      }}
                    >
                      <TicketButton
                        text={TEXTS.Ticket.content[29]}
                        color="#3C3C3C"
                        isActive={false}
                      />
                    </Box>
                  </motion.div>

                  {/* チケットぴあ最速先行 (抽選) */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontWeight="bold"
                      fontSize={{
                        base: "14px",
                        sm: "20px",
                        md: "28px",
                        lg: "35px",
                        xl: "35px",
                      }}
                      mt={{
                        base: -3,
                        sm: -3,
                        md: 0,
                        lg: 0,
                      }}
                    >
                      <Flex align="center" justify="center" gap={0}>
                        {TEXTS.Ticket.content[16]}
                        <Text
                          fontSize={{
                            base: "10px",
                            sm: "14px",
                            md: "20px",
                            lg: "25px",
                            xl: "25px",
                          }}
                          fontWeight="normal"
                        >
                          {TEXTS.Ticket.content[26]}
                        </Text>
                      </Flex>
                    </Text>
                  </motion.div>

                  {/* 受付期間 */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                        "2xl": "24px",
                      }}
                      mb={{
                        base: -7,
                        sm: -7,
                        md: -6,
                        lg: -6,
                      }}
                    >
                      {TEXTS.Ticket.content[17]}
                    </Text>
                  </motion.div>

                  {/* 申し込みボタン */}
                  <motion.div
                    variants={staggerChildVariants}
                    style={{ marginTop: "0px" }}
                  >
                    <Box
                      mt={{
                        base: "-10px",
                        sm: "-10px",
                        md: "-10px",
                        lg: "-12px",
                      }}
                    >
                      <TicketButton
                        text={TEXTS.Ticket.content[15]}
                        color="rgb(60, 18, 9)"
                        href="https://w.pia.jp/t/topgun-maverick-concert/"
                        target="_blank"
                        rel="noopener noreferrer"
                        isActive={true}
                      />
                    </Box>
                  </motion.div>

                  {/* チケットぴあ二次先行 */}
                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontWeight="bold"
                      fontSize={{
                        base: "14px",
                        sm: "20px",
                        md: "28px",
                        lg: "35px",
                        xl: "35px",
                      }}
                      mt={{
                        base: -3,
                        sm: -3,
                        md: 0,
                        lg: 0,
                      }}
                    >
                      <Flex align="center" justify="center" gap={0}>
                        {TEXTS.Ticket.content[19]}
                        <Text
                          fontSize={{
                            base: "10px",
                            sm: "14px",
                            md: "20px",
                            lg: "25px",
                            xl: "25px",
                          }}
                          fontWeight="normal"
                        >
                          {TEXTS.Ticket.content[26]}
                        </Text>
                      </Flex>
                    </Text>
                  </motion.div>

                  <motion.div variants={staggerChildVariants}>
                    <Text
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                        "2xl": "24px",
                      }}
                      mt={{
                        base: -1,
                        sm: -1,
                        md: -1,
                        lg: 0,
                        xl: 0,
                        "2xl": 0,
                      }}
                      mb={{
                        base: -7,
                        sm: -7,
                        md: -6,
                        lg: -6,
                      }}
                      textAlign="center"
                    >
                      {TEXTS.Ticket.content[20]}
                    </Text>
                  </motion.div>

                  <motion.div
                    variants={staggerChildVariants}
                    style={{ marginTop: "0px" }}
                  >
                    <Box
                      mt={{
                        base: "-10px",
                        sm: "-10px",
                        md: "-10px",
                        lg: "-12px",
                      }}
                    >
                      <TicketButton
                        text={TEXTS.Ticket.content[21]}
                        color="#3C3C3C"
                        isActive={false}
                      />
                    </Box>
                  </motion.div>
                </VStack>
              </motion.div>
            </Box>

            {/* 一般販売 */}
            <Box
              bg="rgb(91, 63, 48)"
              px={{
                base: 4,
                sm: 6,
                md: 8,
                lg: 10,
              }}
              py={{
                base: 2,
                sm: 2,
                md: 2,
                lg: 2,
              }}
              mt={{
                base: "2",
                sm: "4",
                md: "6",
                lg: "10",
              }}
              width="100%"
              mx="auto"
            >
              <motion.div
                ref={refs.generalSaleTitleRef.ref}
                initial="hidden"
                animate={
                  refs.generalSaleTitleRef.inView
                    ? scrollDirection === "up"
                      ? "instant"
                      : "visible"
                    : "hidden"
                }
                variants={{
                  hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut",
                      delay: 0.45,
                    },
                  },
                  instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                }}
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "14px",
                    sm: "20px",
                    md: "28px",
                    lg: "35px",
                    xl: "35px",
                  }}
                  fontWeight="bold"
                  textAlign="center"
                >
                  {TEXTS.Ticket.content[22]}
                </Text>
              </motion.div>
            </Box>

            <Box bg="rgb(105, 82, 63)" width="100%" mx="auto" mt={-2} py={4}>
              <VStack spacing={3} align="center" width="100%">
                {/* 販売時期 */}
                <motion.div
                  ref={refs.generalSaleContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSaleContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.4,
                        staggerChildren: 0.15,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <Text
                      as={motion.div}
                      variants={staggerChildVariants}
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "20px",
                        md: "28px",
                        lg: "35px",
                        xl: "35px",
                      }}
                      fontWeight="bold"
                      textAlign="center"
                    >
                      <Flex align="center" justify="center" gap={0}>
                        {TEXTS.Ticket.content[23]}
                        <Text
                          fontSize={{
                            base: "10px",
                            sm: "14px",
                            md: "20px",
                            lg: "25px",
                            xl: "25px",
                          }}
                          fontWeight="normal"
                        >
                          {TEXTS.Ticket.content[27]}
                        </Text>
                      </Flex>
                    </Text>

                    {/* 日時情報 */}
                    <Text
                      as={motion.div}
                      variants={staggerChildVariants}
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                        "2xl": "24px",
                      }}
                      textAlign="center"
                    >
                      {TEXTS.Ticket.content[24]}
                    </Text>
                  </VStack>
                </motion.div>

                {/* ボタン */}
                <motion.div
                  ref={refs.generalSaleButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSaleButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                        delay: 0.55,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={{ marginTop: "-5px" }}
                >
                  <Box
                    mt={{
                      base: "-27px",
                      sm: "-27px",
                      md: "-39px",
                      lg: "-42px",
                      xl: "-42px",
                      "2xl": "-42px",
                    }}
                  >
                    <TicketButton
                      text={TEXTS.Ticket.content[25]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>
              </VStack>
            </Box>
          </VStack>
        </motion.div>
      </Box>

      {/* ここから独立したアニメーションを持つテキストセクション */}
      <Box
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
        }}
        mx="auto"
        mt={{
          base: 4,
          sm: 4,
          md: 8,
          lg: 10,
        }}
      >
        <Flex
          gap={{
            base: 2,
            sm: 2,
            md: 8,
            lg: 10,
          }}
          flexDirection={{
            base: "column",
            md: "row",
          }}
          align={{
            base: "center",
            md: "flex-start",
          }}
          justify="center"
          width="100%"
        >
          <motion.div
            ref={refs.ticketContent8Ref.ref}
            initial="hidden"
            animate={
              refs.ticketContent8Ref.inView ||
              elementsAnimatedState.ticketContent8
                ? "visible"
                : "hidden"
            }
            variants={unifiedVariants}
            onAnimationComplete={() => {
              if (refs.ticketContent8Ref.inView) {
                elementsAnimatedState.ticketContent8 = true;
              }
            }}
          >
            <Text
              color="white"
              fontSize={{
                base: "10px",
                sm: "10px",
                md: "16px",
                lg: "22px",
                xl: "22px",
              }}
              border="1px solid white"
              px={2}
              py={1}
              height="fit-content"
            >
              {TEXTS.Ticket.content[8]}
            </Text>
          </motion.div>
          <Box mt={{ base: 0, md: "-2" }}>
            <Text
              color="white"
              fontSize={{
                base: "16px",
                sm: "20px",
                md: "28px",
                lg: "35px",
                xl: "35px",
              }}
              fontWeight="bold"
            >
              <motion.div
                ref={refs.ticketContent9Ref.ref}
                initial="hidden"
                animate={
                  refs.ticketContent9Ref.inView ||
                  elementsAnimatedState.ticketContent9
                    ? "visible"
                    : "hidden"
                }
                variants={unifiedVariants}
                onAnimationComplete={() => {
                  if (refs.ticketContent9Ref.inView) {
                    elementsAnimatedState.ticketContent9 = true;
                  }
                }}
              >
                {TEXTS.Ticket.content[9]}
              </motion.div>
            </Text>
            <Text
              color="white"
              fontSize={{
                base: "10px",
                sm: "10px",
                md: "10px",
                lg: "16px",
                xl: "16px",
              }}
            >
              <motion.div
                ref={refs.ticketContent10Ref.ref}
                initial="hidden"
                animate={
                  refs.ticketContent10Ref.inView ||
                  elementsAnimatedState.ticketContent10
                    ? "visible"
                    : "hidden"
                }
                variants={unifiedVariants}
                onAnimationComplete={() => {
                  if (refs.ticketContent10Ref.inView) {
                    elementsAnimatedState.ticketContent10 = true;
                  }
                }}
              >
                {TEXTS.Ticket.content[10]}
              </motion.div>
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default TicketSection;
